import { Button, Grid, TextField as MuiTextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Field, Form, Formik } from "formik";
import { CheckboxWithLabel, TextField } from "formik-mui";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { IsEnabled } from "src/components/permissions";
import type { CaseGeneralSettingsMutationData } from "src/hooks/cases";
import { getCutoffTranslation, useRunYears } from "src/hooks/runYearHooks";
import { useCaseGeneralSettingsSchema } from "src/schemas/case-general-settings.schema";
import type { APIError, Case, GeneralPermissions } from "src/types";

export interface CaseGeneralSettingsValues {
  id: number;
  name: string;
  description: string;
  runYear: number;
  ospmEnabled: boolean;
}

interface Props {
  case: Case;
  readOnly?: boolean;
  isLoading: boolean;
  errors?: APIError[] | null;
  permissions: GeneralPermissions;
  onClose: () => void;
  onSubmit: (values: CaseGeneralSettingsMutationData) => void;
}

export function CaseGeneralSettingsForm(props: Props) {
  const { t } = useTranslation();
  const { data: runYears } = useRunYears();
  const validationSchema = useCaseGeneralSettingsSchema();
  const cutoffLabel =
    getCutoffTranslation(
      runYears?.find((runYear) => runYear.year === props.case.runYear)
        ?.cutoffList,
      props.case.cutoff
    ) ?? props.case.cutoff;
  const initialValues: CaseGeneralSettingsValues = {
    id: props.case.id,
    name: props.case.name,
    description: props.case.description,
    ospmEnabled: props.case.ospmEnabled,
    runYear: props.case.runYear,
  };

  return render();

  function render() {
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values: CaseGeneralSettingsValues) => {
          return props.onSubmit(values);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="name"
                  label={t("label.name")}
                  type="text"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="description"
                  label={t("label.description")}
                  multiline
                  rows={5}
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  label={t("label.runYear")}
                  value={props.case.runYear}
                  disabled
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  label={t("label.trafficNetwork")}
                  value={cutoffLabel}
                  disabled
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <IsEnabled permission={props.permissions.cases.startOspm}>
                <Grid item xs={12}>
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    name="ospmEnabled"
                    Label={{ label: "OSPM" }}
                    disabled={props.readOnly}
                  />
                </Grid>
              </IsEnabled>
              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}
              {!props.readOnly && (
                <ButtonGrid
                  container
                  item
                  xs={12}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.cancel")}
                  </Button>
                  <ButtonLoading
                    isLoading={isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                  >
                    {t("label.save")}
                  </ButtonLoading>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
